<template>
  <BaseCard>
    <div class="payment-wrapper" :class="{ 'is-loading': loading }">
      <!-- No payment state -->
      <div v-if="!loading && !hasPayment" class="no-payment">
        <h2 class="sidasta-heading">
          {{ t('frontpage_latestpayment') }}
        </h2>
        <div class="no-payment-content">
          <p class="no-payment-text">
            {{ t('frontpage_no_payment_text') }}
          </p>
        </div>
      </div>

      <!-- Has payment state or loading -->
      <div v-else>
        <h2 class="sidasta-heading">
          {{ t('frontpage_latestpayment') }}
        </h2>
        <div class="gauge-wrapper">
          <svg
            viewBox="0 0 200 100"
            class="gauge"
            >
            <!-- Background arc -->
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#E0E0E0"
              stroke-width="20"
            />
            <!-- Animated foreground arc -->
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#00877C"
              stroke-width="20"
              :stroke-dasharray="`${animatedLength} ${circumference}`"
              stroke-dashoffset="0"
            />
          </svg>
        </div>

        <!-- Values -->
        <div class="amount-grid">
          <!-- Iðgjald -->
          <div class="amount-block">
            <div class="label-with-dot">
              <span class="dot dot-dark" />
              <span class="label">{{ t('frontpage_idgjold_header') }}</span>
            </div>
            <div class="value">
                {{ currency(idgjald) }} kr.
            </div>
          </div>

          <!-- Mótframlag -->
          <div class="amount-block">
            <div class="label-with-dot">
              <span class="dot dot-light" />
              <span class="label">{{ t('sameignTable_motFramlag') }}</span>
            </div>
            <div class="value">
                {{ currency(motframlag) }} kr.
            </div>
          </div>

          <!-- Samtals -->
          <div class="amount-block no-dot">
            <div class="label">
                {{ t('sameignTable_samtals') }}
                </div>
                <div class="value">
                    {{ currency(total) }} kr.
                </div>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useCurrency } from '@/composables/useCurrency'
import BaseCard from '@/components/shared/BaseCard.vue'
import { useI18n } from 'vue-i18n'

const { currency } = useCurrency()

interface Props {
  idgjald: number
  motframlag: number
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const { t } = useI18n()

const hasPayment = computed(() => {
  return props.idgjald > 0 || props.motframlag > 0
})

const total = computed(() => props.idgjald + props.motframlag)
const percentage = computed(() => {
  if (total.value === 0) return 0
  return (props.idgjald / total.value) * 100
})

const circumference = 251.2
const animatedLength = ref(0)

function startGaugeAnimation() {
  const targetLength = (percentage.value / 100) * circumference
  let current = 0

  const animate = () => {
    current += 4
    if (current >= targetLength) {
      animatedLength.value = targetLength
    } else {
      animatedLength.value = current
      requestAnimationFrame(animate)
    }
  }

  animatedLength.value = 0 // reset before animation
  requestAnimationFrame(animate)
}

watch(
  () => [props.idgjald, props.motframlag],
  ([idgjald, motframlag]) => {
    if (idgjald != null && motframlag != null && !props.loading) {
      if (hasPayment.value) {
        startGaugeAnimation()
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
@use "@/scss/settings/variables" as var;

.payment-wrapper {
  position: relative;
  min-height: 300px;
  
  > div:not(.loading-overlay) {
    transition: filter 0.3s ease;
  }
  
  &.is-loading > div:not(.loading-overlay) {
    filter: blur(3px);
    pointer-events: none;
  }
}

.payment-wrapper:has(.no-payment) {
  min-height: auto;
}

.sidasta-heading {
  font-size: var.$font-size-lg;
  font-weight: var.$font-weight-bold;
  margin-bottom: 24px;
  color: var.$color-text;
}

.gauge-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 32px;
}

.gauge {
  width: 100%;
  max-width: 300px;
  height: auto;
}

.amount-grid {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid var.$color-border;
  padding-top: 24px;
  gap: 16px;
}

.amount-block {
  flex: 1;
  text-align: center;

  &:not(:last-child) {
    border-right: 1px solid var.$color-border;
  }

  .label {
    font-size: var.$font-size-sm;
    color: var.$color-text-muted;
    line-height: 24px;
  }

  .value {
    font-size: var.$font-size-lg;
    font-weight: var.$font-weight-bold;
  }
}

.label-with-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 4px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;

  &.dot-dark {
    background-color: #00877C;
  }

  &.dot-light {
    background-color: #DADADA;
  }
}

.no-dot .label-with-dot {
  display: none;
}

.no-payment {
  text-align: center;
  padding: 20px 0;

  .sidasta-heading {
    margin-bottom: 32px;
  }

  .no-payment-content {
    padding: 40px 20px;
    border-radius: 8px;
  }

  .no-payment-text {
    font-size: var.$font-size-base;
    color: var.$color-text-muted;
    margin: 0;
    line-height: 1.6;
  }
}

// Dark mode
body.dark-mode {
  .payment-wrapper.is-loading > div:not(.loading-overlay) {
    filter: blur(3px);
// Dark mode
body.dark-mode {
  .payment-wrapper.is-loading > div:not(.loading-overlay) {
    filter: blur(3px);
  }
  
  .sidasta-heading {
    color: var.$color-text-dark;
  }   background-color: rgba(255, 255, 255, 0.05);
    }

    .no-payment-text {
      color: var.$color-text-muted-dark;
    }
  }

  .amount-grid {
    border-top-color: var.$color-border-dark;
  }

  .amount-block {
    &:not(:last-child) {
      border-right-color: var.$color-border-dark;
    }

    .label {
      color: var.$color-text-muted-dark;
    }

    .value {
      color: var.$color-text-dark;
    }
  }

  .gauge path:first-child {
    stroke: #3a3a3a;
  }
}
</style>